// ============================================================================
// @alloyhead/core — Readback SAB Layout
// ============================================================================
// Schema-driven layout for the readback SharedArrayBuffer. The overlay reads
// engine state (dirty, selection, transform, strokeOn, layers) from this buffer
// via DataView at the computed offsets.
//
// Previously these were hand-written magic constants (RB_DIRTY=0, RB_HAS_SELECTION=4, etc.)
// scattered across host-bridge.ts. Now they're computed from a field list so adding
// a new field can't introduce offset collisions or alignment bugs.
//
// Usage:
//   import { READBACK, READBACK_SAB_SIZE } from './readback-layout.js';
//   view.setUint32(READBACK.dirty, 1, true);
//   view.setFloat32(READBACK.selX, 42.5, true);
// ============================================================================
const READBACK_FIELDS = [
    // --- Core flags ---
    { name: 'dirty', type: 'u32' },
    // --- Selection ---
    { name: 'hasSelection', type: 'u32' },
    { name: 'selX', type: 'f32' },
    { name: 'selY', type: 'f32' },
    { name: 'selW', type: 'f32' },
    { name: 'selH', type: 'f32' },
    // --- Transform ---
    { name: 'hasTransform', type: 'u32' },
    { name: 'tfmData', type: 'f32', count: 16 }, // x,y,w,h,rot,sx,sy,selOnly + 8 reserved
    // --- Stroke ---
    { name: 'strokeOn', type: 'u32' },
    // --- Layers ---
    { name: 'layerCount', type: 'u32' },
    { name: 'activeLayer', type: 'u32' },
    // --- Reserved for future use ---
    { name: '_reserved', type: 'u32', count: 6 },
];
const TYPE_SIZE = { u32: 4, f32: 4 };
function computeReadbackLayout(fields) {
    const offsets = {};
    let cursor = 0;
    for (const field of fields) {
        const size = TYPE_SIZE[field.type];
        // Align to field size
        const rem = cursor % size;
        if (rem !== 0)
            cursor += size - rem;
        offsets[field.name] = cursor;
        cursor += size * (field.count ?? 1);
    }
    // Align total to 8 bytes
    const totalSize = Math.ceil(cursor / 8) * 8;
    return { offsets, totalSize };
}
const { offsets, totalSize } = computeReadbackLayout(READBACK_FIELDS);
/**
 * Computed byte offsets for readback SAB fields.
 * Use with DataView: `view.getUint32(READBACK.dirty, true)`
 */
export const READBACK = offsets;
/** Total size of the readback SAB in bytes. */
export const READBACK_SAB_SIZE = totalSize;
/**
 * The field definitions — exposed for documentation/tooling.
 */
export const READBACK_FIELDS_DEF = READBACK_FIELDS;
//# sourceMappingURL=readback-layout.js.map